import {
  getCookieConsent,
  setCookieConsent,
} from '@/shared/lib/localStorage/cookieConsent';

import {isCookieConsentExpired} from './lib/isCookieConsentExpired';
import {CookieConsent, CookieConsentCategories} from './types';

type CookieConsentReader = () => CookieConsent | null;
type CookieConsentWriter = (consent: CookieConsent) => void;

interface CookieConsentServiceDeps {
  readConsent: CookieConsentReader;
  writeConsent: CookieConsentWriter;
}

type PendingDecision = {
  promise: Promise<CookieConsent>;
  resolve: (value: CookieConsent) => void;
};

class CookieConsentService {
  private readonly readConsent: CookieConsentReader;
  private readonly writeConsent: CookieConsentWriter;
  private decision: CookieConsent | null = null;
  private pendingDecision: PendingDecision | null = null;

  constructor({readConsent, writeConsent}: CookieConsentServiceDeps) {
    this.readConsent = readConsent;
    this.writeConsent = writeConsent;
  }

  public createConsent(
    categories: Omit<CookieConsentCategories, 'essential'>
  ): CookieConsent {
    return {
      timestamp: Date.now(),
      categories: {
        essential: true,
        statistics: categories.statistics,
        marketing: categories.marketing,
      },
    };
  }

  public initFromStorage(): CookieConsent | null {
    const storedConsent = this.readFromStorage();
    if (!storedConsent) {
      this.decision = null;
      return null;
    }

    this.setDecision(storedConsent);

    return storedConsent;
  }

  public readFromStorage(): CookieConsent | null {
    const storedConsent = this.readConsent();
    if (!storedConsent) {
      return null;
    }

    if (isCookieConsentExpired(storedConsent)) {
      return null;
    }

    return storedConsent;
  }

  public saveDecision(consent: CookieConsent): void {
    this.writeConsent(consent);
    this.setDecision(consent);
  }

  public waitForDecision(): Promise<CookieConsent> {
    if (this.decision) {
      return Promise.resolve(this.decision);
    }

    if (this.pendingDecision) {
      return this.pendingDecision.promise;
    }

    this.pendingDecision = this.createPendingDecision();

    return this.pendingDecision.promise;
  }

  private createPendingDecision(): PendingDecision {
    let resolve!: (value: CookieConsent) => void;
    const promise = new Promise<CookieConsent>((res) => {
      resolve = res;
    });

    return {promise, resolve};
  }

  private setDecision(consent: CookieConsent): void {
    this.decision = consent;
    this.pendingDecision?.resolve(consent);
    this.pendingDecision = null;
  }
}

export const cookieConsentService = new CookieConsentService({
  readConsent: getCookieConsent,
  writeConsent: setCookieConsent,
});
